import * as React from 'react';

import {
  buildResidenceDraft,
  INITIAL_RESIDENCE_DRAFT,
  snapshotResidenceIds,
  type IFrequentLocationsState,
  type IResidenceDraft,
  type IResidenceFormState,
} from './residenceDraft';

import type { IDependantResidences } from '../../../../hooks/queries/useDependantResidences';
import type { IDependant, IDependantPartnership } from '../../../../hooks/queries/useDependants';

export interface IUseResidenceDraftResult {
  draft: IResidenceDraft;
  setLiveTogether: (value: boolean | null) => void;
  setParentAForm: (next: IResidenceFormState) => void;
  setParentBForm: (next: IResidenceFormState) => void;
  setFrequentLocations: (next: IFrequentLocationsState) => void;
  /**
   * Snapshot of every `Residence.id` captured at hydration, for the
   * submit-time orphan diff. A ref (not state) so it's read synchronously in
   * the submit handler and never recomputed from the post-submit refetch.
   */
  hydratedResidenceIdsRef: React.MutableRefObject<ReadonlySet<string>>;
}

/**
 * Owns the in-progress residence draft for the two-page section: a plain
 * `useState` object plus targeted setters, hydrated exactly once from the
 * server. The component never unmounts between the arrangement and details
 * pages, so the draft (and the one-shot guard) survive the page round-trip
 * with no provider involvement — flipping back from details to arrangement
 * keeps every edit. A true remount (resume after abandoning) re-hydrates from
 * fresh server data, which is correct because nothing was persisted.
 *
 * The hydrate-once guard means a later residence-query refetch (e.g. the
 * post-submit refresh) never stomps the user's edits.
 */
export const useResidenceDraft = (
  dependants: readonly IDependant[],
  dependantResidences: readonly IDependantResidences[],
  partnership: IDependantPartnership | null
): IUseResidenceDraftResult => {
  const [draft, setDraft] = React.useState<IResidenceDraft>(INITIAL_RESIDENCE_DRAFT);
  const hasHydratedRef = React.useRef(false);
  const hydratedResidenceIdsRef = React.useRef<ReadonlySet<string>>(new Set());

  React.useEffect(() => {
    if (hasHydratedRef.current || dependants.length === 0 || dependantResidences.length === 0) {
      return;
    }
    hydratedResidenceIdsRef.current = snapshotResidenceIds(dependantResidences);
    setDraft(buildResidenceDraft(dependants, dependantResidences, partnership));
    hasHydratedRef.current = true;
  }, [dependants, dependantResidences, partnership]);

  const setLiveTogether = React.useCallback(
    (value: boolean | null) => setDraft((prev) => ({ ...prev, liveTogether: value })),
    []
  );
  const setParentAForm = React.useCallback(
    (next: IResidenceFormState) => setDraft((prev) => ({ ...prev, parentAForm: next })),
    []
  );
  const setParentBForm = React.useCallback(
    (next: IResidenceFormState) => setDraft((prev) => ({ ...prev, parentBForm: next })),
    []
  );
  const setFrequentLocations = React.useCallback(
    (next: IFrequentLocationsState) => setDraft((prev) => ({ ...prev, frequentLocations: next })),
    []
  );

  return { draft, setLiveTogether, setParentAForm, setParentBForm, setFrequentLocations, hydratedResidenceIdsRef };
};
